import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useAuth0 } from '@auth0/auth0-react'
import {
  XMarkIcon,
  PhotoIcon,
  PlusIcon,
  MinusIcon
} from '@heroicons/react/24/outline'
import { bourbonApi, BourbonListing, UpdateBourbonListingData } from '../services/bourbonApi'
import PhotoUpload from './PhotoUpload'
import toast from 'react-hot-toast'

interface EditBourbonListingModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  listing: BourbonListing | null
}

export default function EditBourbonListingModal({
  isOpen,
  onClose,
  onSuccess,
  listing
}: EditBourbonListingModalProps) {
  const { getAccessTokenSilently } = useAuth0()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formData, setFormData] = useState<UpdateBourbonListingData>({
    type: 'have',
    title: '',
    brand: '',
    age: '',
    proof: undefined,
    category: 'bourbon',
    description: '',
    images: [],
    location: '',
    tradePreferences: ''
  })

  const [errors, setErrors] = useState<{ [key: string]: string }>({})

  // Update form data when listing changes
  useEffect(() => {
    if (listing) {
      setFormData({
        type: listing.type,
        title: listing.title,
        brand: listing.brand || '',
        age: listing.age,
        proof: listing.proof,
        category: listing.category,
        description: listing.description || '',
        images: listing.images || [],
        location: listing.location || '',
        tradePreferences: listing.tradePreferences || ''
      })
    }
  }, [listing])

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {}

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required'
    }

    if (!formData.category) {
      newErrors.category = 'Category is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm() || !listing) {
      return
    }

    try {
      setIsSubmitting(true)
      await bourbonApi.updateListing(listing.id, formData, getAccessTokenSilently)
      toast.success('Listing updated successfully!')
      onSuccess()
    } catch (error) {
      console.error('Error updating listing:', error)
      toast.error('Failed to update listing')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handlePhotoUploaded = (photoUrl: string) => {
    setFormData(prev => ({
      ...prev,
      images: [...(prev.images || []), photoUrl]
    }))
  }

  const handlePhotoRemoved = (index: number) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images?.filter((_, i) => i !== index) || []
    }))
  }

  if (!isOpen || !listing) return null

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[60] overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 transition-opacity bg-black bg-opacity-50 z-[60]"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="inline-block w-full max-w-2xl p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white dark:bg-gray-800 shadow-xl rounded-2xl relative z-[61]"
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                Edit Bourbon Listing
              </h3>
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Type Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Listing Type
                </label>
                <div className="flex space-x-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="have"
                      checked={formData.type === 'have'}
                      onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value as 'have' | 'want' }))}
                      className="w-4 h-4 text-green-600 border-gray-300 focus:ring-green-500"
                    />
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">I have this</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="want"
                      checked={formData.type === 'want'}
                      onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value as 'have' | 'want' }))}
                      className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">I want this</span>
                  </label>
                </div>
              </div>

              {/* Title */}
              <div>
                <label htmlFor="title" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="e.g., Blanton's Single Barrel"
                />
                {errors.title && <p className="mt-1 text-sm text-red-600">{errors.title}</p>}
              </div>

              {/* Brand */}
              <div>
                <label htmlFor="brand" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Brand
                </label>
                <input
                  type="text"
                  id="brand"
                  value={formData.brand}
                  onChange={(e) => setFormData(prev => ({ ...prev, brand: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="e.g., Buffalo Trace"
                />
              </div>

              {/* Age and Proof */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="age" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Age
                  </label>
                  <input
                    type="text"
                    id="age"
                    value={formData.age || ''}
                    onChange={(e) => {
                      const value = e.target.value.trim()
                      // Try to parse as number, otherwise keep as string
                      const numValue = parseFloat(value)
                      setFormData(prev => ({ 
                        ...prev, 
                        age: value === '' ? undefined : (!isNaN(numValue) && value === numValue.toString() ? numValue : value)
                      }))
                    }}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="e.g. 12, NAS, 18 months"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Enter years (e.g. 12) or text (e.g. NAS, 18 months)
                  </p>
                </div>
                <div>
                  <label htmlFor="proof" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Proof
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    id="proof"
                    value={formData.proof || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, proof: e.target.value ? parseFloat(e.target.value) : undefined }))}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="90.4"
                  />
                </div>
              </div>

              {/* Category */}
              <div>
                <label htmlFor="category" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Category <span className="text-red-500">*</span>
                </label>
                <select
                  id="category"
                  value={formData.category}
                  onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="bourbon">Bourbon</option>
                  <option value="rye">Rye Whiskey</option>
                  <option value="scotch">Scotch</option>
                  <option value="irish">Irish Whiskey</option>
                  <option value="japanese">Japanese Whisky</option>
                  <option value="canadian">Canadian Whisky</option>
                  <option value="other">Other</option>
                </select>
                {errors.category && <p className="mt-1 text-sm text-red-600">{errors.category}</p>}
              </div>

              {/* Location */}
              <div>
                <label htmlFor="location" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Location
                </label>
                <input
                  type="text"
                  id="location"
                  value={formData.location}
                  onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="e.g., Louisville, KY"
                />
              </div>

              {/* Description */}
              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description
                </label>
                <textarea
                  id="description"
                  rows={4}
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Tell us about this bourbon..."
                />
              </div>

              {/* Trade Preferences */}
              <div>
                <label htmlFor="tradePreferences" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Trade Preferences
                </label>
                <textarea
                  id="tradePreferences"
                  rows={3}
                  value={formData.tradePreferences}
                  onChange={(e) => setFormData(prev => ({ ...prev, tradePreferences: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="What are you looking for in return?"
                />
              </div>

              {/* Photos */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Photos
                </label>
                
                {/* Existing Photos */}
                {formData.images && formData.images.length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-4">
                    {formData.images.map((image, index) => (
                      <div key={index} className="relative">
                        <img
                          src={image}
                          alt={`Photo ${index + 1}`}
                          className="w-full h-24 object-cover rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={() => handlePhotoRemoved(index)}
                          className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                        >
                          <MinusIcon className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Photo Upload */}
                <PhotoUpload
                  currentPhoto=""
                  onPhotoUploaded={handlePhotoUploaded}
                  onPhotoRemoved={() => {}}
                />
              </div>

              {/* Submit Button */}
              <div className="flex justify-end space-x-4 pt-6">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-6 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-md transition-colors"
                >
                  {isSubmitting ? 'Updating...' : 'Update Listing'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    </AnimatePresence>
  )
}
